import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DaycareService } from '../services/daycareService';
import Navigation from '../components/Navigation';
import '../styles/pages/package-history.css';

const ACTION_META = {
    created:   { label: 'Adquirido',   color: '#dbeafe', text: '#1d4ed8' },
    updated:   { label: 'Actualizado', color: '#f3f4f6', text: '#374151' },
    used:      { label: 'Usado',       color: '#d1fae5', text: '#065f46' },
    completed: { label: 'Completado',  color: '#e0e7ff', text: '#3730a3' },
    cancelled: { label: 'Cancelado',   color: '#fef3c7', text: '#92400e' },
    deleted:   { label: 'Eliminado',   color: '#fee2e2', text: '#991b1b' },
    expired:   { label: 'Vencido',     color: '#fff7ed', text: '#c2410c' },
};

const PER_PAGE = 10;

function formatDateTime(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function DaysTag({ before, after }) {
    if (before == null && after == null) return null;
    if (before == null) return <span className="ph-days">{after} días</span>;
    if (after == null)  return <span className="ph-days">{before} días</span>;
    if (before === after) return <span className="ph-days">{after} días</span>;
    return (
        <span className="ph-days">
            {before} → {after} días
        </span>
    );
}

export default function PackageHistory() {
    const { authorizedFetch } = useAuth();
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState('');
    const [search, setSearch]   = useState('');
    const [filterAction, setFilterAction] = useState('');
    const [page, setPage] = useState(1);

    useEffect(() => {
        DaycareService.getPackageHistory(authorizedFetch)
            .then(data => setHistory(data))
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [authorizedFetch]);

    const filtered = useMemo(() => {
        const term = search.toLowerCase();
        return history.filter(h => {
            if (filterAction && h.action !== filterAction) return false;
            if (!term) return true;
            return (
                h.dogName?.toLowerCase().includes(term) ||
                h.packageType?.toLowerCase().includes(term) ||
                h.performedBy?.username?.toLowerCase().includes(term) ||
                h.note?.toLowerCase().includes(term)
            );
        });
    }, [history, search, filterAction]);

    // Reset to page 1 whenever filters change
    const handleSearch = (val) => { setSearch(val); setPage(1); };
    const handleFilter = (key) => { setFilterAction(prev => prev === key ? '' : key); setPage(1); };

    const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    const paginated  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

    return (
        <div>
            <Navigation />
            <div className="ph-container">
                <div className="ph-header">
                    <div>
                        <h1 className="ph-title">Historial de paquetes</h1>
                        <p className="ph-sub">Registro completo — adquisiciones, uso, cancelaciones y eliminaciones</p>
                    </div>
                    <Link to="/daycare/packages" className="ph-back-link">← Paquetes</Link>
                </div>

                {error && <div className="message error">{error}</div>}

                {/* Filters */}
                <div className="ph-filters">
                    <div className="ph-search-wrap">
                        <span className="ph-search-icon">🔍</span>
                        <input
                            className="ph-search"
                            placeholder="Buscar perro, tipo de paquete, usuario…"
                            value={search}
                            onChange={e => handleSearch(e.target.value)}
                        />
                        {search && (
                            <button className="ph-search-clear" onClick={() => handleSearch('')}>✕</button>
                        )}
                    </div>
                    <div className="ph-chips">
                        <button
                            className={`ph-chip${!filterAction ? ' on' : ''}`}
                            onClick={() => handleFilter('')}
                        >Todos</button>
                        {Object.entries(ACTION_META).map(([key, meta]) => (
                            <button
                                key={key}
                                className={`ph-chip${filterAction === key ? ' on' : ''}`}
                                style={filterAction === key
                                    ? { background: meta.color, color: meta.text, borderColor: meta.text }
                                    : {}}
                                onClick={() => handleFilter(key)}
                            >
                                {meta.label}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <p className="ph-empty">Cargando historial…</p>
                ) : filtered.length === 0 ? (
                    <p className="ph-empty">
                        {search || filterAction ? 'Ningún registro coincide con tu búsqueda.' : 'Sin historial aún.'}
                    </p>
                ) : (
                    <div className="ph-count">
                        {filtered.length} evento{filtered.length !== 1 ? 's' : ''}
                        {totalPages > 1 && ` · página ${page} de ${totalPages}`}
                    </div>
                )}

                <div className="ph-timeline">
                    {paginated.map(entry => {
                        const meta = ACTION_META[entry.action] || ACTION_META.updated;
                        return (
                            <div key={entry._id} className="ph-entry">
                                {/* Left: action badge + connector line */}
                                <div className="ph-entry-left">
                                    <span
                                        className="ph-badge"
                                        style={{ background: meta.color, color: meta.text }}
                                    >
                                        {meta.label}
                                    </span>
                                    <div className="ph-line" />
                                </div>

                                {/* Right: content */}
                                <div className="ph-entry-body">
                                    <div className="ph-entry-top">
                                        <span className="ph-dog">{entry.dogName}</span>
                                        {entry.packageType && (
                                            <span className="ph-pkg-type">{entry.packageType}</span>
                                        )}
                                        <DaysTag
                                            before={entry.daysLeftBefore}
                                            after={entry.daysLeftAfter}
                                        />
                                    </div>
                                    {entry.note && (
                                        <div className="ph-note">{entry.note}</div>
                                    )}
                                    <div className="ph-meta">
                                        <span className="ph-who">
                                            👤 {entry.performedBy?.username || 'Usuario desconocido'}
                                        </span>
                                        <span className="ph-when">
                                            {formatDateTime(entry.createdAt)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {totalPages > 1 && (
                    <div className="ph-pagination">
                        <button
                            className="ph-page-btn"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                        >← Anterior</button>
                        <span className="ph-page-info">Página {page} de {totalPages}</span>
                        <button
                            className="ph-page-btn"
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                        >Siguiente →</button>
                    </div>
                )}
            </div>
        </div>
    );
}
